import {
  getGetFiskalReceiptsQueryKey,
  useGetFiskalReceipts,
  useGetFiskalRegisters,
  useGetFiskalTotalMismatches,
} from '@agos/api-client/fiskal'
import {
  mismatchDifferenceCents,
  mismatchDirection,
  mismatchEmptyNote,
  mismatchSummary,
  type TotalMismatch,
} from './total-mismatch'
import {
  EmployeeAuthModal,
  FISKAL_STORNO,
  OrderNumberLink,
  T,
  fetchOrderDocuments,
  fiskalDocumentCounts,
  fiskalDocumentCountsNote,
  fiskalDocumentEvents,
  fiskalDocumentsOf,
  formatCents,
  formatDateTime,
  lineVatLabel,
  parseMoneyToCents,
  paymentLabel,
  printVirtualReceipt,
  registerIds,
  registerName,
  SHIPPED_REGISTER_NAMES,
  tt,
  type AuthActionMeta,
  useAuth,
  useModalFocus,
} from '@agos/shared'
import {
  BUTTON_GHOST,
  type Column,
  DataTable,
  EmptyState,
  PageHeader,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  toast,
} from '@agos/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Ban,
  ChevronDown,
  ChevronRight,
  FileText,
  Printer,
  Receipt,
  RefreshCw,
  Search,
  X,
  Eye,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  canStornoOnDevice,
  virtualStornoNote,
  driftsFromPrinted,
  filterReceipts,
  type FiskalReceipt,
  linesAddedAfterPrint,
  receiptBreakdown,
  receiptState,
  receiptTotals,
  remedyForDrift,
  STATE_BADGES,
  stornoBlockedReason,
} from './receipt-list'

/**
 * `/fiskal-receipts` — every receipt the registers have printed.
 *
 * §14.2 and §11.4 are what shape this screen. A **virtual** receipt has no UID
 * because it never reached the tax authority's device — a valid state, and 2 177 of
 * the restored receipts are in it — and it therefore cannot be storned on the
 * device either. Legacy offers the storno button anyway and explains afterwards, in
 * a toast, once the register has already refused. This one says so first.
 *
 * Ported from legacy `pages/fiskal-receipts.tsx`.
 */

/**
 * What the two registers this port shipped with are called, until a franchise types a name over one.
 * §10A.35 leaves `fiskal_settings.label` NULL deliberately: writing these strings into rows would
 * freeze one translation into data.
 */

const RECEIPT_LIMIT = 100

const STATE_TABS = [
  { id: 'all', label: tt('Všetky') },
  { id: 'valid', label: tt('Platné') },
  { id: 'virtual', label: tt('Virtuálne') },
  { id: 'storned', label: tt('Stornované') },
] as const

const FILTER_CLASSES =
  'px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

export function FiskalReceiptsPage() {
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()

  const [kasaId, setKasaId] = useState('restauracia')
  const [state, setState] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [detail, setDetail] = useState<FiskalReceipt | null>(null)
  const [stornoTarget, setStornoTarget] = useState<FiskalReceipt | null>(null)
  /* §9.463 - shut by default, like §9.459's panel and for the same reason: it is a list somebody opens
   * to act on, and a sticky preference would show the next visit a state nobody chose. */
  const [mismatchesOpen, setMismatchesOpen] = useState(false)

  /* FB-837, §9.551 - **`isError`, because *this register printed nothing in this filter* is a claim about the
     register.** Legacy shows a destructive toast on this read's failure (*Nepodarilo sa načítať doklady*) and AGOS drew
     its empty state instead, on a tax surface. */
  const {
    data,
    isLoading,
    isError: receiptsFailed,
    refetch: refetchReceipts,
  } = useGetFiskalReceipts({ kasaId, limit: RECEIPT_LIMIT })
  // §10A.35 — a tab per configured register, so a third one's receipts can be read at all. The two
  // the port shipped with are the fallback until the call answers, never an empty strip.
  const { data: registerData } = useGetFiskalRegisters()
  // §9.463 - the printed total against the stored one. Not filtered by register: a document on either
  // one disagreeing with its order is the same question, and the row names its own kasa.
  /* FB-837, §9.551 - **this one is evidential, which is what makes it the worst of the three.**
     `mismatchEmptyNote` does not say *nothing found*: it says the check compares the document's total with the order's
     and starts from the first printed document. From a read that never answered, that is an assurance about fiscal
     compliance. AGOS built this panel (§9.463) and legacy has no equivalent, so the claim is entirely ours. */
  const {
    data: mismatchData,
    isError: mismatchesFailed,
    refetch: refetchMismatches,
  } = useGetFiskalTotalMismatches()
  const mismatches = useMemo(
    () => (Array.isArray(mismatchData) ? (mismatchData as TotalMismatch[]) : []),
    [mismatchData],
  )
  /* §9.578, FB-880 - hoisted, because the list and the detail panel label a receipt's register too
   * and both used `kasaLabel`'s two-entry map. So this screen's tab strip already said what the
   * franchise calls a third register while every row under it said a raw `kasa_id`. */
  const registerRows = useMemo(
    () =>
      Array.isArray(registerData)
        ? (registerData as Array<{ kasaId?: string; label?: string }>)
        : [],
    [registerData],
  )
  const registerTabs = useMemo(
    () =>
      registerIds(registerData).map((id) => ({
        id,
        label: registerName(id, registerRows, SHIPPED_REGISTER_NAMES),
      })),
    [registerData, registerRows],
  )

  const receipts = useMemo(() => {
    const payload = data as { receipts?: unknown } | undefined
    const rows = payload?.receipts ?? []
    return Array.isArray(rows) ? (rows as FiskalReceipt[]) : []
  }, [data])

  const shown = useMemo(() => filterReceipts(receipts, { state, search }), [receipts, state, search])
  const totals = receiptTotals(receipts)
  const canStorno = hasPermission('fiskal') || hasPermission('fiskal_storno')

  /**
   * §9.151's seven columns, in legacy's order — §9.201 moved them into the shared table.
   *
   * Sorting where a sort means something: when it was printed, which order it belongs to, and what
   * it came to. Not on *Akcie*, and not on *Stav*, whose order is a badge's and not a value's.
   */
  const receiptColumns = useMemo<Column<FiskalReceipt>[]>(
    () => [
      {
        key: 'printedAt',
        header: tt('Dátum tlače'),
        sortable: true,
        sortValue: (receipt) => receipt.fiskalPrintedAt ?? '',
        render: (receipt) => (
          <span className="whitespace-nowrap text-muted-foreground text-xs">
            {receipt.fiskalPrintedAt ? formatDateTime(receipt.fiskalPrintedAt) : ''}
            <span className="block text-[10px]">{registerName(receipt.fiskalKasaId, registerRows, SHIPPED_REGISTER_NAMES)}</span>
          </span>
        ),
      },
      {
        key: 'order',
        header: tt('Objednávka'),
        sortable: true,
        sortValue: (receipt) => receipt.orderNumber ?? 0,
        render: (receipt) => (
          // §9.143 — the order number is the way back to the archive.
          <OrderNumberLink
            orderId={receipt.id}
            className="font-mono text-xs font-bold text-primary hover:underline"
            title={`${tt('Otvoriť v archíve')}: #${receipt.orderNumber ?? ''}`}
          >
            #{receipt.orderNumber}
          </OrderNumberLink>
        ),
      },
      {
        key: 'receiptNumber',
        header: tt('Číslo dokladu'),
        sortable: true,
        sortValue: (receipt) => receipt.fiskalReceiptNumber ?? '',
        render: (receipt) => (
          <span className="font-mono text-xs">{receipt.fiskalReceiptNumber ?? '-'}</span>
        ),
      },
      {
        key: 'total',
        header: tt('Suma'),
        headerClassName: 'text-right',
        className: 'text-right',
        sortable: true,
        sortValue: (receipt) => parseMoneyToCents(receipt.total),
        render: (receipt) => {
          const remedy = remedyForDrift(receipt)
          return (
            <span className="whitespace-nowrap">
              <span className="font-bold tabular-nums">
                {formatCents(parseMoneyToCents(receipt.total))}
              </span>
              {/* What AGOS knows and legacy does not stays under *Suma*, because that is the number
                  it qualifies. */}
              {driftsFromPrinted(receipt) && (
                <span
                  className="block text-[10px] font-semibold text-amber-500"
                  data-printed-total-drift={receipt.id}
                  title={tt('Doklad bol vytlačený na inú sumu, než akú objednávka uvádza teraz')}
                >
                  <T>doklad</T> {formatCents(parseMoneyToCents(receipt.printedTotal))}
                </span>
              )}
              {remedy !== null && (
                <span
                  className="block text-[10px] text-muted-foreground font-normal"
                  data-drift-remedy={receipt.id}
                >
                  {remedy}
                </span>
              )}
              {linesAddedAfterPrint(receipt) > 0 && (
                <span
                  className="block text-[10px] font-semibold text-amber-500"
                  data-lines-after-print={receipt.id}
                  title={tt('Objednávka dostala položky po vytlačení dokladu')}
                >
                  +{linesAddedAfterPrint(receipt)} <T>po doklade</T>
                </span>
              )}
            </span>
          )
        },
      },
      {
        key: 'payment',
        header: tt('Platba'),
        sortable: true,
        sortValue: (receipt) => paymentLabel(receipt.paymentMethod),
        render: (receipt) => (
          <span className="text-xs" data-receipt-payment={receipt.id}>
            {paymentLabel(receipt.paymentMethod)}
          </span>
        ),
      },
      {
        key: 'state',
        header: tt('Stav'),
        render: (receipt) => {
          const badge = STATE_BADGES[receiptState(receipt)]
          return <StatusBadge color={badge.color} label={badge.label} />
        },
      },
      {
        key: 'actions',
        header: tt('Akcie'),
        headerClassName: 'text-right',
        className: 'text-right',
        render: (receipt) => {
          const blocked = stornoBlockedReason(receipt)
          return (
            <span className="whitespace-nowrap">
              <button
                type="button"
                onClick={() => setDetail(receipt)}
                aria-label={`${tt('Detail dokladu')} #${receipt.orderNumber}`}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() =>
                  printVirtualReceipt(
                    {
                      orderNumber: receipt.orderNumber,
                      issuedAt: receipt.fiskalPrintedAt,
                      paymentMethod: receipt.paymentMethod,
                      total: receipt.total,
                      vatRate: receipt.vatRate,
                      vatBase: receipt.vatBase,
                      vatAmount: receipt.vatAmount,
                      items: receipt.items,
                    },
                    Date.now(),
                  )
                }
                aria-label={`${tt('Vytlačiť')} #${receipt.orderNumber}`}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
              </button>
              {canStorno && (
                <button
                  type="button"
                  onClick={() => setStornoTarget(receipt)}
                  disabled={!canStornoOnDevice(receipt)}
                  title={blocked ?? tt('Stornovať fiškálny doklad')}
                  aria-label={`${tt('Stornovať')} #${receipt.orderNumber}`}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Ban className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          )
        },
      },
    ],
    [canStorno, registerRows],
  )

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetFiskalReceiptsQueryKey() })

  const stornoMeta: AuthActionMeta | null = stornoTarget
    ? {
        orderId: stornoTarget.id ?? 0,
        orderNumber: stornoTarget.orderNumber ?? '',
        orderTotalCents: parseMoneyToCents(stornoTarget.total),
        newStatus: FISKAL_STORNO,
      }
    : null

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Fiškálne doklady')}
        description={tt('Doklady vytlačené kasami.')}
        icon={<Receipt className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={refresh}
            aria-label={tt('Obnoviť')}
            className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        }
      />

      <TabGroup
        tabs={registerTabs}
        active={kasaId}
        onChange={setKasaId}
      />

      {/* §9.463, BACKEND.md §10A.226 - the comparison nothing has ever made: what the register
        * printed against what the order stored. It exists because the owner's answer to #63 was *"it
        * varied by person"*, which made the 439 historical orders unrepairable and left this as the
        * thing that stops it recurring.
        *
        * **Always drawn, unlike §10A.207's panel.** That one hides when empty because *every payment
        * has a session* is a sentence people stop reading; this one is empty **by construction** until
        * documents accumulate after `V59`, so a reader who cannot see it has no way to learn it is
        * working. The fold keeps it quiet: the button carries the count and the worst difference. */}
      <section
        className="rounded-2xl border border-border bg-muted/20 p-3 space-y-2"
        data-total-mismatches
      >
        <button
          type="button"
          onClick={() => setMismatchesOpen((open) => !open)}
          aria-expanded={mismatchesOpen}
          data-total-mismatches-toggle
          className="flex w-full flex-wrap items-baseline justify-between gap-2 text-left"
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            {mismatchesOpen ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            <T>Doklad nesúhlasí s objednávkou</T>
          </span>
          {/* FB-837, §9.551 - the failure arm is FIRST, because `mismatches` is empty on a failure too and drawn
              second this branch never runs (§9.532's trap). And `0` in the muted colour is a fault styled as
              reassurance - FB-810's shape, which is why the colour moves with the claim rather than the count. */}
          <span
            className={cn(
              'text-[11px] font-bold tabular-nums',
              mismatchesFailed
                ? 'text-destructive'
                : mismatches.length > 0
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground',
            )}
            data-total-mismatches-summary
          >
            {mismatchesFailed ? <T>nezistené</T> : mismatches.length === 0 ? '0' : mismatchSummary(mismatches)}
          </span>
        </button>

        {/* The panel keeps its row rather than vanishing: silencing the one control that surfaces a discrepancy is the
            worse answer, so it says it could not check instead of saying there is nothing to check. */}
        {mismatchesOpen && mismatchesFailed && (
          <p className="text-[11px] text-destructive leading-relaxed" data-total-mismatches-error>
            <T>Kontrolu sa nepodarilo načítať, takže o rozdieloch sa nedá nič povedať.</T>{' '}
            <button
              type="button"
              onClick={() => void refetchMismatches()}
              data-total-mismatches-retry
              className="underline font-semibold"
            >
              <T>Skúsiť znova</T>
            </button>
          </p>
        )}

        {mismatchesOpen && !mismatchesFailed && mismatches.length === 0 && (
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-total-mismatches-empty>
            {mismatchEmptyNote()}
          </p>
        )}

        {mismatchesOpen && mismatches.length > 0 && (
          <div className="space-y-1">
            {mismatches.map((row) => (
              <div
                key={row.orderId ?? row.receiptNumber}
                data-total-mismatch={String(row.orderId ?? 0)}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs"
              >
                <span className="font-semibold">#{row.orderNumber}</span>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {row.receiptNumber ?? '-'}
                </span>
                <span className="tabular-nums">
                  <T>doklad</T> {formatCents(row.printedCents ?? 0)}
                </span>
                <span className="tabular-nums">
                  <T>objednávka</T> {formatCents(row.recordedCents ?? 0)}
                </span>
                <span className="tabular-nums font-bold text-amber-600 dark:text-amber-400">
                  {mismatchDifferenceCents(row) > 0 ? '+' : ''}
                  {formatCents(mismatchDifferenceCents(row))}
                </span>
                <span className="text-muted-foreground">{mismatchDirection(row)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={tt('Dokladov')} value={String(totals.count)} icon={FileText} color="violet" />
        <StatCard
          label={tt('Obrat')}
          value={formatCents(totals.grossCents)}
          icon={Receipt}
          color="emerald"
        />
        <StatCard label="DPH" value={formatCents(totals.vatCents)} icon={FileText} color="blue" />
        <StatCard
          label={tt('Virtuálnych')}
          value={String(totals.virtualCount)}
          icon={AlertTriangle}
          color="amber"
        />
      </div>

      {totals.stornoCount > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {/* A storned receipt was reversed, so it is counted but not banked.
              Legacy sums every row into the takings. */}
          <T>Stornované doklady sa počítajú, ale nie sú v obrate:</T> {totals.stornoCount}
        </p>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tt('Číslo objednávky, dokladu, položka...')}
            aria-label={tt('Hľadať doklad')}
            className={cn(FILTER_CLASSES, 'w-full pl-9')}
          />
        </div>
        <select
          value={state}
          onChange={(event) => setState(event.target.value)}
          aria-label={tt('Stav dokladu')}
          className={FILTER_CLASSES}
        >
          {STATE_TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-1.5">
          {[1, 2, 3, 4].map((placeholder) => (
            <div
              key={placeholder}
              className="h-14 rounded-xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : receiptsFailed ? (
        /* FB-837, §9.551 - before the empty state, not beside it: `shown` is empty on a failure too, so drawn second
           this branch is unreachable and *nič nevytlačila* is back. */
        <div className="bg-card border border-destructive/40 rounded-2xl p-12" data-receipts-error>
          <EmptyState
            icon={<Receipt className="w-10 h-10" />}
            title={tt('Doklady sa nepodarilo načítať')}
            description={tt('Nevieme, čo táto kasa vytlačila. Skúste to znova.')}
            action={
              <button
                type="button"
                onClick={() => void refetchReceipts()}
                data-receipts-retry
                className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground text-sm"
              >
                <T>Skúsiť znova</T>
              </button>
            }
          />
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Receipt className="w-10 h-10" />}
            title={tt('Žiadne doklady')}
            description={tt('Táto kasa v tomto filtri nič nevytlačila.')}
          />
        </div>
      ) : (
        <div data-receipts>
          {/* §9.201 - the shared `DataTable`, not a hand-rolled one. Twenty-odd screens use it, and
              this table wrote its own head, padding and hover - so it was not styled differently, it
              was a different component, without sorting, paging or the card layout a narrow screen
              gets. §9.151's seven columns and their order are unchanged; what moved is the frame.

              `rowAttributes` is why the conversion was possible without rewriting the specs:
              `[data-receipt-row]` is how `fiskal-receipts.spec.ts` finds a row. */}
          <DataTable
            columns={receiptColumns}
            data={shown}
            // `id` is optional on the wire; the index is the fallback so a row without one is
            // still keyed rather than crashing the list it belongs to.
            rowKey={(receipt, index) => receipt.id ?? index}
            rowAttributes={(receipt) => ({ 'data-receipt-row': receipt.id })}
            emptyMessage={tt('Žiadne doklady')}
            compact
          />
        </div>
      )}

      {detail && <ReceiptDetail receipt={detail} onClose={() => setDetail(null)} />}

      {/* Owner decision #38 - the button is offered on a virtual receipt now, so the limit is said
        * beside the password prompt rather than after the fact: AGOS records the reversal and the
        * register still needs a visit. Without it somebody presses it, sees success, and believes
        * the tax office has been told. */}
      {stornoTarget !== null && virtualStornoNote(stornoTarget) !== '' && (
        <p
          className="fixed inset-x-0 bottom-4 z-[60] mx-auto w-[min(32rem,92vw)] rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-600 dark:text-amber-500"
          data-virtual-storno-note
        >
          {virtualStornoNote(stornoTarget)}
        </p>
      )}

      {stornoMeta && (
        <EmployeeAuthModal
          meta={stornoMeta}
          onClose={() => setStornoTarget(null)}
          onSuccess={(_confirmedBy, response) => {
            setStornoTarget(null)
            refresh()
            // The storno response is not one of the typed order responses, so it is read by name.
            const storno = response as { message?: unknown; virtual?: unknown } | undefined
            const message = typeof storno?.message === 'string' ? storno.message : undefined
            if (storno?.virtual === true) {
              toast({
                title: tt('Storno označené lokálne'),
                description:
                  message ?? tt('Doklad bol vystavený vo virtuálnom režime - kasa o ňom nevie.'),
                duration: 8000,
              })
              return
            }
            toast({
              title: tt('Storno vykonané'),
              description: message ?? tt('Fiškálny doklad bol stornovaný.'),
            })
          }}
        />
      )}
    </div>
  )
}

function ReceiptDetail({
  receipt,
  onClose,
}: {
  receipt: FiskalReceipt
  onClose: () => void
}) {
  /* §9.578 - the franchise's own register names. Shared by query key with the page above, so this is
   * no extra request, and a refused read falls back to the shipped names rather than to nothing. */
  const { data: registerData } = useGetFiskalRegisters()
  /* Memoised, because the column list below is memoised and reads it: a fresh array every render
   * would either make that memo recompute every render or - if it were left out of the dependency
   * list - keep labelling rows from the registers as they were before the call answered. Lint caught
   * the second, which is the one a person would not notice. */
  const registerRows = useMemo(
    () =>
      Array.isArray(registerData)
        ? (registerData as Array<{ kasaId?: string; label?: string }>)
        : [],
    [registerData],
  )
  /* §9.488, `TODO.md` 3c - the trap and the Escape. Unconditional: the preview holds no request. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)
  const breakdown = receiptBreakdown(receipt)
  const badge = STATE_BADGES[receiptState(receipt)]

  return (
    /* §9.488 - a read-only dialog is still a dialog: it takes the attention and has to be dismissible, and
       what it shows is a tax document. The wrapper **is** the scrim here, so it carries the role and the card
       inside carries the trap - `ConfirmDialog`'s pattern. Named with `aria-label` carrying the order number,
       because an operator may open two in sequence and *Doklad* alone would not say which. */
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${tt('Doklad')} #${receipt.orderNumber ?? ''}`}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Receipt className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            {/* §9.143 - a tax document, and the order it was printed for. The list row itself
              * is already a button that opens this modal, so the link belongs here. */}
            <p className="font-bold">
              <OrderNumberLink
                orderId={receipt.id}
                className="text-primary hover:underline"
                title={`${tt('Otvoriť v archíve')}: #${receipt.orderNumber ?? ''}`}
              >
                #{receipt.orderNumber}
              </OrderNumberLink>
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {receipt.fiskalReceiptNumber ?? tt('bez čísla dokladu')} ·{' '}
              {registerName(receipt.fiskalKasaId, registerRows, SHIPPED_REGISTER_NAMES)}
            </p>
          </div>
          <StatusBadge color={badge.color} label={badge.label} />
          <button
            type="button"
            data-dialog-cancel
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* §9.569 - the generated `FiskalReceipt` carries `id` as optional, so the panel is drawn only
            * when there is an order to ask about. A history keyed on a guessed id would be somebody else's. */}
          {receipt.id !== undefined && <DocumentHistory orderId={receipt.id} />}

          {receipt.fiskalStornoAt && (
            <p className="text-xs text-red-500">
              <T>Stornovaný</T> {formatDateTime(receipt.fiskalStornoAt)}
              {receipt.fiskalStornoBy ? ` · ${receipt.fiskalStornoBy}` : ''}
            </p>
          )}

          <div className="space-y-1">
            {(receipt.items ?? []).map((line, index) => (
              <div key={`${line.productName}-${index}`} className="flex justify-between text-sm">
                <span className="min-w-0 truncate">
                  <span className="text-primary font-bold">{line.quantity}×</span>{' '}
                  {line.productName}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {/* §9.5's DPH bullet. A receipt is the document a customer is handed and a tax
                      office reads, so the letter each line was registered under is stated on every
                      one - unlike the order detail, which draws it only on a mixed-rate order. The
                      register prints the letter, not the percentage, and a reconciliation groups by
                      it, which is what FB-192 cost when a 19 % line went out under A. */}
                  {lineVatLabel(line) !== '' && (
                    <span className="text-[11px] text-muted-foreground" data-receipt-line-vat>
                      {lineVatLabel(line)}
                    </span>
                  )}
                  <span className="tabular-nums">
                    {formatCents(parseMoneyToCents(line.totalPrice))}
                  </span>
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-border pt-2 space-y-1 text-sm">
            <Row label={tt('Medzisúčet')} value={formatCents(breakdown.subtotalCents)} />
            {breakdown.discountCents > 0 && (
              <Row label={tt('Zľava')} value={`−${formatCents(breakdown.discountCents)}`} />
            )}
            {breakdown.deliveryCents > 0 && (
              <Row label={tt('Dovoz')} value={formatCents(breakdown.deliveryCents)} />
            )}
            <Row
              label={`${tt('Základ DPH')} (${receipt.vatRate ?? 0}%)`}
              value={formatCents(breakdown.vatBaseCents)}
            />
            <Row label={`DPH (${receipt.vatRate ?? 0}%)`} value={formatCents(breakdown.vatCents)} />
            <div className="flex justify-between font-bold pt-1 border-t border-border">
              <span>
                <T>Celkom</T>
              </span>
              <span className="text-primary tabular-nums">
                {formatCents(breakdown.totalCents)}
              </span>
            </div>
          </div>

          {breakdown.inconsistent && (
            <p className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              {/* Surfaced rather than rounded away - a receipt whose tax lines do
                  not add up to its own total is a document somebody has to see. */}
              <T>Základ a DPH nedávajú dokopy celkovú sumu tohto dokladu.</T>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

/**
 * Every fiscal document this order has had - §9.569, FB-864.
 *
 * **The owner's ask**: *"when you click on the specific line, show whole history of printed/storned
 * fiskal for that specific order"*, and *"add there one more thing, how much fiskal were storned and
 * reprinted."*
 *
 * **This screen could not have shown it before §10A.294.** `FiskalReceiptHistory` reads `orders`, so
 * the row this dialog was opened from carries the order's **latest** stamp - which §10A.191's own
 * migration comment names as the limitation `fiskal_documents` exists to lift.
 *
 * **Empty is a real answer and says so.** An order printed before `V59` has no rows, and a panel that
 * rendered nothing would read as a failed load - §9.529's class, on a tax screen.
 */
function DocumentHistory({ orderId }: { orderId: number }) {
  const query = useQuery({
    queryKey: ['fiskal-order-documents', orderId],
    queryFn: () => fetchOrderDocuments(orderId),
  })

  if (query.isLoading) {
    return (
      <p className="text-xs text-muted-foreground" data-documents-loading>
        <T>Načítavam históriu dokladov…</T>
      </p>
    )
  }
  if (query.isError) {
    /* §9.529 - a failed read must not look like an order with no documents, which on a tax screen is a
       positive claim about what the register did. */
    return (
      <p className="text-xs text-red-500" data-documents-error>
        <T>Históriu dokladov sa nepodarilo načítať.</T>
      </p>
    )
  }

  const documents = fiskalDocumentsOf(query.data)
  const events = fiskalDocumentEvents(documents)
  const counts = fiskalDocumentCounts(documents)
  const note = fiskalDocumentCountsNote(counts)

  if (documents.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-documents-none>
        <T>Pre túto objednávku nie je zaznamenaný žiadny doklad (staršie ako evidencia dokladov).</T>
      </p>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-muted/30 px-3 py-2" data-documents={String(documents.length)}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
        <T>História dokladov</T>
      </p>
      {note !== '' && (
        <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 mb-1.5" data-documents-note>
          {note}
        </p>
      )}
      <ol className="space-y-1">
        {events.map((event, index) => (
          <li
            key={`${event.kind}-${String(event.document.id)}-${String(index)}`}
            className="flex items-baseline justify-between gap-2 text-[11px]"
            data-document-event={event.kind}
          >
            <span className={event.kind === 'stornoed' ? 'text-red-500 font-semibold' : 'text-foreground'}>
              {event.kind === 'stornoed' ? tt('Stornovaný') : tt('Vytlačený')}
              {event.document.receiptNumber === null ? '' : ` · ${event.document.receiptNumber}`}
              {event.kind === 'stornoed' && event.document.stornoBy !== null
                ? ` · ${event.document.stornoBy}`
                : ''}
            </span>
            <span className="shrink-0 text-muted-foreground">
              {event.document.totalCents === null ? '' : `${formatCents(event.document.totalCents)} · `}
              {formatDateTime(event.at)}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}
